import styled from 'styled-components'
import dayjs from 'dayjs'
import { Text } from '@lmc/ui-kit/src/general'
import { getMedicationIcon } from '../utils/medicationUtils'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { SectionHeader } from '../componentsV2/SectionHeader'

type RecordEntry = {
  id: string
  date: string
  user: {
    name: string
  }
  witness?: {
    name: string
  }
  action: string
  description: string
  editedBy?: {
    name: string
    date: string
  }
}

type RecordsSectionProps = {
  records?: RecordEntry[]
}

// mocked until BE's records are implemented

const mockRecords: RecordEntry[] = [
  {
    id: '1',
    date: '2025-12-23T16:53:00.000Z',
    user: {
      name: 'Richard K. Oates'
    },
    witness: {
      name: 'Jolie Jolie'
    },
    action: 'Administered',
    description:
      "For Jolie Aw's 08:00 scheduled meds, 1 tablet x Panadol was administered",
    editedBy: {
      name: 'Madison Coles',
      date: '2025-12-23T16:53:00.000Z'
    }
  },
  {
    id: '2',
    date: '2025-12-23T16:53:00.000Z',
    user: {
      name: 'Richard K. Oates'
    },
    witness: {
      name: 'Jolie Jolie'
    },
    action: 'Add stock',
    description: 'For Panadol, 20 tablets were added'
  }
]

const RecordsSection = ({ records = mockRecords }: RecordsSectionProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const formatTimestamp = (date: string) => {
    return dayjs(date).format('ddd DD-MM-YYYY HH:mm')
  }

  const formatDate = (date: string) => {
    return dayjs(date).format('DD-MM-YYYY')
  }

  return (
    <RecordsContainer>
      <SectionHeader title={t('RECORDS')} />
      <RecordsList>
        {records.map((record) => (
          <RecordCard key={record.id}>
            <RecordContent>
              <RecordIconContainer>
                {getMedicationIcon(undefined, 'small')}
              </RecordIconContainer>
              <RecordDetails>
                <RecordTimestamp level={2}>
                  {formatTimestamp(record.date)} · {record.user.name}
                  {record.witness &&
                    `, ${t('WITNESSED_BY')} ${record.witness.name}`}
                </RecordTimestamp>
                <RecordAction>{record.action}</RecordAction>
                <RecordDescription level={2}>
                  {record.description}
                </RecordDescription>
                {record.editedBy && (
                  <RecordEditInfo level={2}>
                    {t('EDITED_BY')} {record.editedBy.name} {t('ON')}{' '}
                    {formatDate(record.editedBy.date)}
                  </RecordEditInfo>
                )}
              </RecordDetails>
            </RecordContent>
          </RecordCard>
        ))}
      </RecordsList>
    </RecordsContainer>
  )
}

const RecordsContainer = styled.div`
  margin-top: 32px;
`

const RecordsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
  padding-bottom: 24px;
`

const RecordCard = styled.div`
  background-color: ${({ theme }) => theme.color.neutral.color11};
  border-radius: 8px;
  padding: 16px;
  max-width: 640px;
`

const RecordContent = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`

const RecordIconContainer = styled.div`
  flex-shrink: 0;
  width: 24px;
  height: 100%;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
`

const RecordDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const RecordTimestamp = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
  font-weight: 500;
`

const RecordAction = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
  font-weight: 700;
`

const RecordDescription = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
  font-weight: 500;
`

const RecordEditInfo = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
  font-weight: 500;
`

export default RecordsSection
